import { ChangeDetectionStrategy, Component, InputSignal, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

export type NoticeCardTone = 'info' | 'warning' | 'error';

const TONE_ICONS: Record<NoticeCardTone, string> = {
  info: 'info_outline',
  warning: 'warning_amber',
  error: 'error_outline',
};

/**
 * Inline notice banner: `info` (neutral surface) for context, `warning`
 * (amber) for action-required, `error` for blocking problems. Projected
 * content stacks vertically, so multi-line bodies need no wrapper.
 */
@Component({
  selector: 'lib-notice-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule],
  template: `
    <div class="notice-card tone-{{ tone() }}">
      <mat-icon>{{ icon() || toneIcon }}</mat-icon>

      <div class="notice-body">
        <ng-content />
      </div>
    </div>
  `,
  // Token fallbacks are deliberate here: only the office-ui theme defines the
  // --warning-* / --mat-sys-* custom properties, and this component must still
  // render sensibly in apps on the shared `optimal` theme, which instead sets
  // --notice-card-bg / --notice-card-fg for its dark theme.
  styles: `
    :host {
      display: block;
    }

    .notice-card {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.75rem 1rem;
      border: 1px solid transparent;
      border-radius: 4px;
      font-size: 0.875rem;
      background: var(--notice-card-bg, var(--mat-sys-surface-variant, rgba(0, 0, 0, 0.05)));
      color: var(--notice-card-fg, var(--mat-sys-on-surface-variant, rgba(0, 0, 0, 0.6)));

      &.tone-warning {
        background: var(--warning-bg, #fff8e1);
        border-color: var(--warning-outline, #e6c34a);
        color: var(--warning-fg, #7a5900);
      }

      &.tone-error {
        color: var(--mat-sys-error, #b00020);
      }

      mat-icon {
        flex-shrink: 0;
      }
    }

    .notice-body {
      display: flex;
      flex-direction: column;
      min-width: 0;
    }
  `,
})
export class NoticeCardComponent {
  public readonly tone: InputSignal<NoticeCardTone> = input<NoticeCardTone>('info');

  /** Material icon name; overrides the tone's default icon. */
  public readonly icon: InputSignal<string> = input<string>('');

  protected get toneIcon(): string {
    return TONE_ICONS[this.tone()];
  }
}
